// Import Vuex and Firebase functions
import { createStore } from 'vuex'
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import {
  doc,
  onSnapshot
} from "firebase/firestore";
import { auth, db } from "@/firebase/config";
import { watchEffect } from "vue";

// Create Vuex store
const store = createStore({
  state: {
    user: null, // Current authenticated user
    userProfile: null, // Profile information of authenticated user
    authIsReady: false, // Flag to track if auth status is ready
    currentRoute: null, // Current route in the application
    versionNumber: 'V 3.1.0', // Version number of the application
    apiUrl: process.env.VUE_APP_API_URL || 'https://customer-portal-api.neutralfuels.net/',
    portalAdminCustomer: null, // PORTAL_SUPER_ADMIN's currently-viewed customer (customers_mcs-shaped)
  },
  getters: {
    isAuthenticated: state => !!state.user && state.authIsReady, // Computed property to check if the user is authenticated
    isCustomer: state => !!state.userProfile && state.userProfile.account_type === 'CUSTOMER',
    isSupplier: state => !!state.userProfile && state.userProfile.account_type === 'SUPPLIER',
    isPortalSuperAdmin: state => !!state.userProfile && state.userProfile.role === 'PORTAL_SUPER_ADMIN'
  },
  mutations: {
    // Mutations to update the state
    setUser(state, payload) {
      state.user = payload
      //console.log('User state changed: ', state.user)
    },
    setAuthIsReady(state, payload) {
      state.authIsReady = payload
      //console.log('Auth is ready')
    },
    setUserProfile(state, payload) {
      state.userProfile = payload
      //console.log('User profile state changed: ', state.userProfile)
    },
    setCurrentRoute(state, payload) {
      state.currentRoute = payload
      //console.log('Current route updated: ', state.currentRoute)
    },
    setPortalAdminCustomer(state, payload) {
      state.portalAdminCustomer = payload
      try {
        const uid = state.user?.uid
        if (uid) {
          const key = `nf.portalAdmin.customer.${uid}`
          if (payload) sessionStorage.setItem(key, JSON.stringify(payload))
          else sessionStorage.removeItem(key)
        }
      } catch (e) {
        // Storage unavailable — selection just won't survive a refresh.
      }
    }
  },
  actions: {
    // Action for logging in a user
    async login(context, { email, password }) {
      //console.log('Login Action')
      const res = await signInWithEmailAndPassword(auth, email, password)
      if (res) {
        let docRef = doc(db, 'Users', res.user.uid)
        const unsub2 = onSnapshot(docRef, doc => {
          //console.log(doc.data().role)
          if (doc.data()) {
            console.log('User Profile: ', doc.data())
            context.commit('setUser', res.user)
            context.commit('setUserProfile', { ...doc.data() })

          } else {
            //console.log('Invalid User')
            signOut(auth)

          }
        }, (err) => {
          //console.log(err.message)
        })
        watchEffect((onInvalidate) => {
          onInvalidate(() => unsub2()) // unsubscribe from the realtime listener
        })
      } else {
        throw Error('Could not complete login')
      }
    },
    // Action for logging out a user
    async logout(context) {
      //console.log('Logout Action')
      context.commit('setPortalAdminCustomer', null)
      await signOut(auth)

      context.commit('setUserProfile', null)
      context.commit('setUser', null)
    },
    // Action to check authentication state
    checkAuth({ commit }) {
      return new Promise((resolve) => {
        auth.onAuthStateChanged(user => {
          commit('setUser', user);

          if (user) {
            try {
              const saved = sessionStorage.getItem(`nf.portalAdmin.customer.${user.uid}`)
              if (saved) commit('setPortalAdminCustomer', JSON.parse(saved))
            } catch (e) {
              // Corrupted/unavailable storage — start unselected.
            }
            let docRef = doc(db, 'Users', user.uid)
            const unsub2 = onSnapshot(docRef, doc => {
              if (doc.data()) {
                commit('setUserProfile', { ...doc.data() })
              } else {
                //console.log('Invalid User')
                signOut(auth)
              }
            }, (err) => {
              //console.log(err.message)
            })
            watchEffect((onInvalidate) => {
              onInvalidate(() => unsub2()) // unsubscribe from the realtime listener
            })
          }
          commit('setAuthIsReady', true);
          resolve(user);
        });
      });
    },
  },
  modules: {
    // Vuex modules can be added here
  }
})

// // Waiting for auth and updating user state
// const unsub = onAuthStateChanged(auth, (user) => {
//   store.commit('setUser', user)
//   if (user) {
//     let docRef = doc(db, 'Users', user.uid)
//     const unsub2 = onSnapshot(docRef, doc => {
//       if (doc.data()) {
//         store.commit('setUserProfile', { ...doc.data() })
//       }
//     }, (err) => {
//       //console.log(err.message)
//     })
//     watchEffect((onInvalidate) => {
//       onInvalidate(() => unsub2()) // unsubscribe from the realtime listener
//     })
//     store.commit('setAuthIsReady', true)
//   }
//   unsub() // unsubscribe from the auth state listener
// })

// Exporting the store for use in the main application
export default store
